import { useCallback, useMemo, useState } from 'react';
import { Badge, Text, Button, Group, Stack, Box } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { ColumnDef, Row } from '@tanstack/react-table';
import RepositoryTable from '@ayocore/platform/client/components/RepositoryTable/RepositoryTable';
import { useDealRepository } from '@ayocore/erp/crm/client/rpc';
import type { Deal } from '@ayocore/erp/crm/schemas/deal.schema';
import type { RpcRepositoryClient } from '@ayocore/platform/server/rpc/types';
import { FilterByStatus } from '@ayocore/erp/crm/client/components/Deals/filters/FilterByStatus';
import { FilterByPartner } from '@ayocore/erp/crm/client/components/Deals/filters/FilterByPartner';
import { FilterByOwner } from '@ayocore/erp/crm/client/components/Deals/filters/FilterByOwner';
import { FilterByPartnerManager } from '@ayocore/erp/crm/client/components/Deals/filters/FilterByPartnerManager';
import { FilterByArchived } from '@ayocore/erp/crm/client/components/Deals/filters/FilterByArchived';

type DealExtended = Deal & {
  partner?: { id: number; name?: string; full_name?: string } | null;
  deal_status?: { id: number; name?: string; color?: string | null } | null;
  deal_owner?: { id: number; name?: string; email?: string } | null;
  currency?: { id: number; code?: string; symbol?: string } | null;
  upd_date?: string | null;
  upd_numbers?: string | null;
};

export interface DealPickerModalProps extends Record<string, unknown> {
  /** Ограничение сделками партнёра из внешнего контекста. */
  partnerId?: number | null;
  /**
   * Режим выбора: 'single' — одна сделка, 'multiple' — несколько.
   * При двойном клике в режиме 'single' сразу подтверждает выбор.
   * По умолчанию: 'single'.
   */
  selectionMode?: 'single' | 'multiple';
  /**
   * Вызывается при подтверждении выбора.
   * Всегда возвращает массив Deal[]: при single — массив из 1 элемента.
   */
  onDealSelected: (deals: Deal[]) => void;
  /**
   * Вызывается при отмене (кнопка «Отмена»).
   * Обычно содержит вызов dealPickerModal.close().
   */
  onClose?: () => void;
}

/**
 * DealPickerModal — модальное окно выбора сделок.
 *
 * Отображает список сделок аналогично DealsList, но в режиме пикера:
 * - клик по строке выделяет сделку;
 * - двойной клик в режиме 'single' сразу подтверждает выбор;
 * - кнопка «Выбрать» передаёт выбранные сделки в колбек onDealSelected.
 *
 * Компонент используется через useModal и не содержит обёртки <Modal>.
 * Его можно как открывать отдельной модалкой, так и встраивать в чужую
 * (см. grasias AllocateDealToRowModal): панель с кнопками закреплена снизу,
 * поэтому «Выбрать» остаётся видимой независимо от высоты содержимого.
 *
 * @example
 * ```tsx
 * const dealPicker = useModal(DealPickerModal);
 *
 * dealPicker.open({
 *   title: 'Выбрать сделку',
 *   selectionMode: 'single',
 *   onDealSelected: (deals) => {
 *     console.log(deals[0]);
 *     dealPicker.close();
 *   },
 *   onClose: () => dealPicker.close(),
 * }, { size: 'xl' });
 * ```
 */
const DealPickerModal = ({
  selectionMode = 'single',
  onDealSelected,
  onClose,
  partnerId,
}: DealPickerModalProps) => {
  const [selectedDeals, setSelectedDeals] = useState<DealExtended[]>([]);

  let repository = useDealRepository().withRelations().withUpdColumns().limit(100);
  if (partnerId != null) repository = repository.filterByPartner(partnerId);

  const columns = useMemo(
    (): ColumnDef<DealExtended>[] => [
      {
        id: 'number',
        header: 'Номер',
        accessorKey: 'number',
        size: 160,
        cell: ({ row }) => <Text size='xs'>{row.original.number}</Text>,
      },
      {
        id: 'partner.name',
        header: 'Партнер',
        accessorFn: row => row.partner?.name ?? '',
        enableSorting: true,
        size: 220,
        cell: ({ row }) => {
          const partner = row.original.partner;
          if (!partner) {
            return (
              <Text size='xs' c='dimmed' fs='italic'>
                —
              </Text>
            );
          }
          return <Text size='xs'>{partner.name || partner.full_name || '—'}</Text>;
        },
      },
      {
        id: 'deal_status.name',
        header: 'Статус',
        accessorFn: row => row.deal_status?.name ?? '',
        enableSorting: true,
        size: 150,
        cell: ({ row }) => {
          const status = row.original.deal_status;
          if (!status) {
            return (
              <Text size='xs' c='dimmed' fs='italic'>
                —
              </Text>
            );
          }
          return (
            <Badge
              color={status.color || 'gray'}
              variant='light'
              size='sm'
              style={{ textTransform: 'none', fontWeight: 'normal' }}
            >
              {status.name}
            </Badge>
          );
        },
      },
      {
        id: 'amount',
        header: 'Сумма',
        accessorKey: 'amount',
        enableSorting: true,
        size: 140,
        cell: ({ row }) => {
          const amount = row.original.amount;
          if (amount == null) {
            return (
              <Text size='xs' c='dimmed' fs='italic'>
                —
              </Text>
            );
          }
          const currency = row.original.currency;
          const currencyLabel = currency?.symbol || currency?.code || '₽';
          const formatted = new Intl.NumberFormat('ru-RU').format(Number(amount));
          return (
            <Text size='xs' style={{ textAlign: 'right' }}>
              {formatted} {currencyLabel}
            </Text>
          );
        },
      },
      {
        id: 'deal_owner.name',
        header: 'Ответственный',
        accessorFn: row => row.deal_owner?.name ?? '',
        enableSorting: true,
        size: 180,
        cell: ({ row }) => {
          const owner = row.original.deal_owner;
          if (!owner) {
            return (
              <Text size='xs' c='dimmed' fs='italic'>
                —
              </Text>
            );
          }
          return <Text size='xs'>{owner.name || owner.email || `ID: ${owner.id}`}</Text>;
        },
      },
      {
        id: 'upd_date',
        header: 'Дата УПД',
        accessorKey: 'upd_date',
        enableSorting: true,
        size: 140,
        cell: ({ row }) => {
          const value = row.original.upd_date;
          if (!value) {
            return (
              <Text size='xs' c='dimmed' fs='italic'>
                —
              </Text>
            );
          }
          const date = new Date(value);
          if (Number.isNaN(date.getTime())) {
            return <Text size='xs'>—</Text>;
          }
          return <Text size='xs'>{date.toLocaleDateString('ru-RU')}</Text>;
        },
      },
      {
        id: 'upd_numbers',
        header: '№ УПД',
        accessorKey: 'upd_numbers',
        size: 160,
        cell: ({ row }) => {
          const val = row.original.upd_numbers;
          if (!val) {
            return (
              <Text size='xs' c='dimmed' fs='italic'>
                —
              </Text>
            );
          }
          return <Text size='xs'>{val}</Text>;
        },
      },
    ],
    []
  );

  /** Клик по строке: в режиме single — заменяет выбор, в multiple — переключает */
  const handleRowClick = useCallback(
    (_row: Row<DealExtended>, rowData: DealExtended) => {
      if (selectionMode === 'single') {
        setSelectedDeals([rowData]);
      } else {
        setSelectedDeals(prev =>
          prev.some(d => d.id === rowData.id)
            ? prev.filter(d => d.id !== rowData.id)
            : [...prev, rowData]
        );
      }
    },
    [selectionMode]
  );

  /** Двойной клик в режиме single — сразу подтверждает выбор */
  const handleRowDoubleClick = useCallback(
    (_row: Row<DealExtended>, rowData: DealExtended) => {
      if (selectionMode === 'single') {
        onDealSelected([rowData]);
      }
    },
    [selectionMode, onDealSelected]
  );

  /** Подсвечиваем выбранные строки */
  const getRowProps = useCallback(
    (row: Row<DealExtended>): React.HTMLAttributes<HTMLDivElement> => {
      const isSelected = selectedDeals.some(d => d.id === row.original.id);
      return {
        style: {
          backgroundColor: isSelected ? 'var(--mantine-color-blue-0)' : undefined,
          cursor: 'pointer',
        },
      };
    },
    [selectedDeals]
  );

  const handleConfirm = () => {
    onDealSelected(selectedDeals);
  };

  const selectionHint =
    selectedDeals.length > 0
      ? `Выбрано: ${selectedDeals.length}`
      : selectionMode === 'single'
        ? 'Нажмите на строку или дважды щёлкните для быстрого выбора'
        : 'Выберите одну или несколько сделок';

  return (
    <Stack gap='md'>
      <RepositoryTable<DealExtended>
        id='crm-deals-picker'
        repository={repository as unknown as RpcRepositoryClient<DealExtended>}
        columns={columns}
        filters={[
          FilterByStatus,
          FilterByPartner,
          FilterByPartnerManager,
          FilterByOwner,
          FilterByArchived,
        ]}
        onRowClick={handleRowClick}
        onRowDoubleClick={selectionMode === 'single' ? handleRowDoubleClick : undefined}
        getRowProps={getRowProps}
        tableBodyHeight={320}
        rowHeight={1}
      />

      {/*
        Футер закреплён снизу. Тело модалки (.modalBody) скроллится целиком, и при
        встраивании пикера в чужую модалку с дополнительными полями над ним
        (например комментарий в grasias-обёртке AllocateDealToRowModal) кнопка
        «Выбрать» уходила под нижний край — её не было видно, пока не проскроллишь.
        Sticky держит панель на виду, содержимое уезжает под неё.
      */}
      <Box
        style={{
          position: 'sticky',
          bottom: 0,
          zIndex: 1,
          background: 'var(--mantine-color-body)',
          borderTop: '1px solid var(--mantine-color-gray-3)',
          paddingTop: 'var(--mantine-spacing-xs)',
        }}
      >
        <Group justify='space-between'>
          <Text size='sm' c='dimmed'>
            {selectionHint}
          </Text>
          <Group>
            <Button variant='default' onClick={onClose}>
              Отмена
            </Button>
            <Button
              leftSection={<IconCheck size={14} />}
              onClick={handleConfirm}
              disabled={selectedDeals.length === 0}
            >
              Выбрать
            </Button>
          </Group>
        </Group>
      </Box>
    </Stack>
  );
};

export default DealPickerModal;
